import React, { useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { breakpoints, useBreakpoint, useMatchMedia } from '@frontend/responsiveness';
import { useLocationTimezone } from '@frontend/schedule-helpers';
import { titleCase } from '@frontend/string';
import { isEmpty } from '@frontend/validation';
import { theme } from '@frontend/theme';
import { Avatar, Chip, Text, useAlert, numeric, TextLink } from '@frontend/design-system';
import { AppointmentTypeChip } from '../../../../../../../components/appointment-type-chip';
import { useViewEventOnCalendar } from '../../../../../../../hooks/calendar/use-view-event-on-calendar';
import { useShowRequestCalendarLink } from '../../../../../../../hooks/requests';
import { getScheduleRequestEventId } from '../../../../../../../utils/calendar-helpers';
import { DATE_DISPLAY_FORMAT } from '../../../../../../../utils/date-helpers';
import { useGetScheduleRequest } from '../../hooks/useGetScheduleRequest';
import { getCustomFieldData, getFormattedScheduleRequestAddress, getScheduleRequestBirthDate } from '../../utils';
import { ScheduleRequestDetailsProperty } from '../components/ScheduleRequestDetailsProperty';
import { ScheduleRequestDetailsSpinnerLoader } from '../components/ScheduleRequestDetailsSpinnerLoader';

interface ScheduleRequestDetailsProps {
  scheduleRequestId: string;
  requestVersion: string;
  locationId: string;
  isPaymentsEnabled: boolean;
}

export const ScheduleRequestItemDetails = React.memo(
  ({ scheduleRequestId, requestVersion, locationId, isPaymentsEnabled }: ScheduleRequestDetailsProps) => {
    const alert = useAlert();
    const { t } = useTranslation('scheduleCalendarRequest');
    const size = useBreakpoint();
    const isVerySmallDevice = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
    const isLargeDevice = size === 'large';
    const isSmallDevice = size === 'small' || size === 'xsmall';

    const { formatDateAndTime } = useLocationTimezone(locationId);
    const isV2Request = requestVersion === 'v2';

    const {
      data: scheduleRequestDetails,
      isLoading: isScheduleRequestLoading,
      isError: isScheduleRequestError,
    } = useGetScheduleRequest(scheduleRequestId, requestVersion, locationId);

    const showCalendarLink = useShowRequestCalendarLink({
      providerId: scheduleRequestDetails?.practitionerId ?? '',
      locationId: scheduleRequestDetails?.locationId ?? '',
      isV2Request,
    });
    const { viewOnCalendar } = useViewEventOnCalendar();

    // Fetch appointment type data to get color information
    const { data: appointmentTypesData } = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
      selectedLocationId: locationId,
      selectedLocationIds: [locationId],
      pageConfig: { page: 1, limit: 500 },
      isEnabled: !!scheduleRequestDetails?.appointmentTypeId,
    });

    const { data: providersData } = SchedulerV3.Helpers.useGetProvidersV3Data({
      selectedLocationId: locationId,
      selectedLocationIds: [locationId],
      pageConfig: { page: 1, limit: 500 },
      isEnabled: !!scheduleRequestDetails?.practitionerId,
    });

    const { appointmentTypeColor, appointmentTypeName, providerName } = useMemo(() => {
      const requestedAppointmentTypeDetails = appointmentTypesData?.appointmentTypes?.find(
        (type) => type.id === scheduleRequestDetails?.appointmentTypeId
      );

      const requestedProviderDetails = providersData?.providers?.find(
        (provider) => provider.id === scheduleRequestDetails?.practitionerId
      );

      return {
        appointmentTypeColor: requestedAppointmentTypeDetails?.details?.clr ?? '',
        appointmentTypeName:
          requestedAppointmentTypeDetails?.displayName ??
          requestedAppointmentTypeDetails?.externalName ??
          scheduleRequestDetails?.appointmentType ??
          '',
        providerName:
          requestedProviderDetails?.displayName ??
          requestedProviderDetails?.externalName ??
          scheduleRequestDetails?.requestedOpenings?.[0]?.assets?.providers?.[0] ??
          '',
      };
    }, [
      appointmentTypesData?.appointmentTypes,
      scheduleRequestDetails?.appointmentType,
      scheduleRequestDetails?.appointmentTypeId,
      scheduleRequestDetails?.requestedOpenings,
      providersData?.providers,
      scheduleRequestDetails?.practitionerId,
    ]);

    const birthDate = getScheduleRequestBirthDate(scheduleRequestDetails?.schedulee?.birthDate);

    const renderRequestTimeListText = useMemo<JSX.Element[] | JSX.Element>(() => {
      if (isEmpty(scheduleRequestDetails?.requestedOpenings)) return <Text>-</Text>;

      // Sort list of dates and times by earliest first
      const sortedOpenings = [...(scheduleRequestDetails?.requestedOpenings || [])]
        .map((opening, index) => ({
          ...opening,
          /* We are using the index of the opening to generate a unique event id for each event cards in calendar 
          and we need to use the exact id below to navigate to the correct event card.
          */
          id: index,
        }))
        .sort((a, b) => {
          if (!a.dateTime && !b.dateTime) return 0;
          if (!a.dateTime) return 1;
          if (!b.dateTime) return -1;
          return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
        });

      const allSameDate = sortedOpenings.every((date) =>
        dayjs(date.dateTime).isSame(dayjs(sortedOpenings[0]?.dateTime), 'date')
      );
      const allSameTz = sortedOpenings.every(
        (date) =>
          formatDateAndTime(date.dateTime, 'z', false) === formatDateAndTime(sortedOpenings[0]?.dateTime, 'z', false)
      );

      // Format the sortedOpenings list of dates and times to not duplicate the date and timezone abbreviation if they are all the same. Example, 'Dec 12 2025, 11:00 AM, 12:15 AM, 2:30 PM EST'. Otherwise keep same format for each datetime. Example, 'Dec 12 2025, 11:00 AM EST, Dec 13 2025, 12:15 AM EST'
      return sortedOpenings.map((opening, i, array) => {
        if (!opening.dateTime) {
          return (
            <Text size='medium' key={`no-date-${i}`}>
              {'-'}
            </Text>
          );
        }
        const isCurrentYear = dayjs(opening.dateTime).isSame(dayjs(), 'year');
        const format = isCurrentYear ? 'MMM D, h:mm A' : 'MMM D YYYY, h:mm A';
        const dateString = formatDateAndTime(opening.dateTime, DATE_DISPLAY_FORMAT, false);

        let textElement: React.ReactNode = null;

        if (allSameDate && allSameTz) {
          const showTzAbbr = i === array.length - 1;
          const fullDate = formatDateAndTime(opening.dateTime, format, showTzAbbr);
          const time = formatDateAndTime(opening.dateTime, 'h:mm A', showTzAbbr);
          textElement = (
            <Text size='medium' key={opening.dateTime} color={showCalendarLink ? 'primary' : 'inherit'}>
              {i === 0 ? fullDate : time}
            </Text>
          );
        }

        textElement = (
          <Text size='medium' key={opening.dateTime} color={showCalendarLink ? 'primary' : 'inherit'}>
            {formatDateAndTime(opening.dateTime, format, true)}
          </Text>
        );

        return (
          <>
            {!showCalendarLink ? (
              textElement
            ) : (
              <TextLink
                key={opening.dateTime}
                textAlign='left'
                onClick={() => viewOnCalendar(getScheduleRequestEventId(scheduleRequestId, opening.id), dateString)}
              >
                {textElement}
              </TextLink>
            )}
          </>
        );
      });
    }, [
      formatDateAndTime,
      scheduleRequestId,
      scheduleRequestDetails?.requestedOpenings,
      showCalendarLink,
      viewOnCalendar,
    ]);

    useEffect(() => {
      if (isScheduleRequestError) {
        alert.error(t('Failed to load appointment request details'));
      }
    }, [alert, isScheduleRequestError, t]);

    const scheduleRequestCustomFieldsData = getCustomFieldData(scheduleRequestDetails?.schedulee?.insuranceInfo, [
      'gender',
      'isNewUser',
    ]);

    const getGridColumnConfiguration = (): React.CSSProperties => {
      if (isVerySmallDevice) return { gridTemplateColumns: 'auto' };
      else if (isSmallDevice) return { gridTemplateColumns: 'auto auto' };
      else if (isLargeDevice) return { gridTemplateColumns: '120px 120px 120px' };
      else return { gridTemplateColumns: 'minmax(auto, 120px) minmax(auto, 120px) minmax(auto, 120px)' };
    };

    return (
      <section css={scheduleRequestContainerStyles}>
        {isScheduleRequestLoading ? (
          <ScheduleRequestDetailsSpinnerLoader />
        ) : (
          <>
            <div css={scheduleRequestGridStyles(getGridColumnConfiguration())}>
              <ScheduleRequestDetailsProperty
                label={t('Appointment Type')}
                value={<AppointmentTypeChip color={appointmentTypeColor} name={appointmentTypeName} />}
              />
              <ScheduleRequestDetailsProperty
                label={t('Provider')}
                value={
                  <Chip
                    color='blue'
                    leftElement={<Avatar size='xxs' name={providerName} />}
                    css={{ border: 0, color: 'inherit' }}
                  >
                    {providerName}
                  </Chip>
                }
              />
              <ScheduleRequestDetailsProperty label={t('Requested Times')} value={renderRequestTimeListText} />
              <ScheduleRequestDetailsProperty label={t('Birthdate')} value={birthDate} />
              <ScheduleRequestDetailsProperty
                label={t('Gender')}
                value={titleCase(scheduleRequestDetails?.schedulee?.gender ?? '') || '-'}
              />
              <ScheduleRequestDetailsProperty
                label={t('Phone Number')}
                value={formatPhoneNumber(scheduleRequestDetails?.schedulee?.phoneNumber ?? '') ?? '-'}
              />
              <ScheduleRequestDetailsProperty
                label={t('Email')}
                value={scheduleRequestDetails?.schedulee?.email ?? '-'}
              />
              <ScheduleRequestDetailsProperty
                label={t('Address')}
                value={getFormattedScheduleRequestAddress(scheduleRequestDetails?.schedulee?.address) ?? '-'}
              />
              <ScheduleRequestDetailsProperty
                label={t('New User')}
                value={scheduleRequestDetails?.schedulee?.existingUser ? t('No') : t('Yes')}
              />
              {isPaymentsEnabled && (
                <ScheduleRequestDetailsProperty
                  label={t('Paid')}
                  value={
                    !!scheduleRequestDetails?.bookingAmount && !!scheduleRequestDetails?.paymentTransactionId ? (
                      <Chip variant='warn' leftElement={<Icon name='dollar-sign-small' />}>
                        {numeric(scheduleRequestDetails?.bookingAmount, { decimals: 2 })}
                      </Chip>
                    ) : (
                      <>-</>
                    )
                  }
                />
              )}
              {!isEmpty(scheduleRequestCustomFieldsData) &&
                Object.entries(scheduleRequestCustomFieldsData).map(([key, value]) => (
                  <ScheduleRequestDetailsProperty key={key} label={key} value={value} />
                ))}
            </div>
            <ScheduleRequestDetailsProperty
              css={{ marginTop: theme.spacing(2) }}
              label={t('Notes')}
              value={scheduleRequestDetails?.note ? scheduleRequestDetails?.note : '-'}
            />
          </>
        )}
      </section>
    );
  }
);

ScheduleRequestItemDetails.displayName = 'ScheduleRequestItemDetails';

const scheduleRequestContainerStyles = css({
  borderRadius: theme.borderRadius.small,
  background: theme.colors.white,
});

const scheduleRequestGridStyles = (gridTemplateColumns: React.CSSProperties) =>
  css({
    display: 'grid',
    gap: theme.spacing(2),
    ...gridTemplateColumns,
  });
